import {
  call, takeLatest, put, select, delay,
} from 'redux-saga/effects';
import { api } from '@/utils/api';
import { CloudflareActionTypes } from './cloudflare.types';
import { cloudflareActions } from './index';
import logger from '@/utils/logger';
import {
  COUNTRY,
} from '@/config';
import {
  assembledCloudflareDomains, assembledFeatures, assembledNewRule, assembledRulesList,
  deleteRuleOnList,
  editRuleOnList,
} from './cloudflare.assembly';
import { CHARLIE_CLOUDFLARE_TIMERS } from '@/config/GrowthBook/constants';


function* requestDomains(action) {
  const { payload } = action;
  const { hostingId } = payload;


  const {
    token: whmcsToken,
  } = yield select(state => state.auth);


  const url = `/api/v3/cloudflare/zones/${hostingId}`;

  const headers = {
    Authorization: `Bearer ${whmcsToken}`,
    'x-brand': COUNTRY,
  };

  const request = { url, headers };

  yield put(cloudflareActions.domains.domainsLoading(true));
  try {
    const response = yield call(api.whmcs.get, request);

    const sorted = assembledCloudflareDomains(response.data.data);
    yield put(cloudflareActions.domains.domainsSet(sorted));
    yield put(cloudflareActions.domains.domainsSuccess());
    yield put(cloudflareActions.domains.domainsError(false));
  } catch (e) {
    const error = { sagasError: e, userRequestData: request, errorMessage: e.message };
    logger.error('Error sagas requestCloudflareDomains', error);
    yield put(cloudflareActions.domains.domainsFailure());
    yield put(cloudflareActions.domains.domainsError(true));
  } finally {
    yield put(cloudflareActions.domains.domainsLoading(false));
  }
}

function* requestActiveDomains() {
  const {
    token: whmcsToken,
  } = yield select(state => state.auth);

  const url = '/api/v3/cloudflare/active_domains';

  const headers = {
    Authorization: `Bearer ${whmcsToken}`,
    'x-brand': COUNTRY,
  };

  const request = { url, headers };

  yield put(cloudflareActions.domains.active.isLoading(true));
  try {
    const response = yield call(api.whmcs.get, request);

    yield put(cloudflareActions.domains.active.set(response.data));
    yield put(cloudflareActions.domains.active.success());
  } catch (e) {
    const error = { sagasError: e, userRequestData: request, errorMessage: e.message };
    logger.error('Error sagas requestCloudflareActiveDomains', error);
    yield put(cloudflareActions.domains.active.failure());
  } finally {
    yield put(cloudflareActions.domains.active.isLoading(false));
  }
}

function* activateCdn(action) {
  const { payload } = action;
  const {
    hostingId,
    domain,
    domainOrigin,
    skipRequest,
    handleInternalDomainWithExternalProviderStatus,
  } = payload;


  const {
    token: whmcsToken,
  } = yield select(state => state.auth);

  const url = '/api/v3/cloudflare/zone/create';

  const headers = {
    Authorization: `Bearer ${whmcsToken}`,
    'x-brand': COUNTRY,
  };

  const request = {
    url,
    headers,
    data: {
      hosting_id: hostingId,
      domain,
      domain_origin: domainOrigin,
    },
  };

  yield put(cloudflareActions.cdn.setLoadingActivation(true));
  try {
    const statusToMantainRequest = ['inactive', 'inactive_delay'];
    const canSkip = !!skipRequest && !!skipRequest.status
      && !statusToMantainRequest.includes(skipRequest.status)
      && skipRequest.cloudflare_ns1
      && skipRequest.cloudflare_ns2;

    if (canSkip) {
      yield put(cloudflareActions.cdn.setActivatedDomain({
        ...skipRequest,
        requestStatus: 'success',
        domain,
      }));
    }

    if (!canSkip) {
      const response = yield call(api.whmcs.post, request);

      if (response.data.data.domain_origin === 'external') {
        handleInternalDomainWithExternalProviderStatus();
        yield put(cloudflareActions.cdn.setActivatedDomain({
          ...response.data.data,
          requestStatus: 'success',
          domain,
        }));
      }

      if (response.data.data.domain_origin !== 'external') {
        yield put(cloudflareActions.cdn.setActivatedDomain({
          ...response.data.data,
          requestStatus: 'success',
          domain,
          status: 'success',
        }));
      }
    }

    yield put(cloudflareActions.cdn.succeededActivation());
    yield put(cloudflareActions.cdn.setLoadingActivation(false));
  } catch (e) {
    const error = { sagasError: e, userRequestData: request, errorMessage: e.message };
    logger.error('Error sagas activateCdn', error);

    const errorObject = {
      domain,
      status: 'failure',
      requestStatus: 'failure',
    };

    if (e.response && e.response.data && e.response.data.data && e.response.data.data.message) {
      errorObject.message = e.response.data.data.message;
    }
    yield put(cloudflareActions.cdn.setActivatedDomain(errorObject));
    yield put(cloudflareActions.cdn.faliedActivation());
    yield put(cloudflareActions.cdn.setLoadingActivation(false));
  }
}

function* deactivateCdn(action) {
  const { payload } = action;
  const {
    hostingId, domain, domainOrigin, enqueueActivation, newHosting,
  } = payload;


  const {
    token: whmcsToken,
  } = yield select(state => state.auth);

  const url = '/api/v3/cloudflare/zone/inactivate';

  const headers = {
    Authorization: `Bearer ${whmcsToken}`,
    'x-brand': COUNTRY,
  };

  const request = {
    url,
    headers,
    data: {
      hosting_id: hostingId,
      domain,
      domain_origin: domainOrigin,
    },
  };

  yield put(cloudflareActions.cdn.setLoadingActivation(true));
  try {
    yield call(api.whmcs.put, request);
    if (!enqueueActivation) {
      yield put(cloudflareActions.cdn.setActivatedDomain({
        status: 'success',
        requestStatus: 'success',
        domain,
      }));
      yield put(cloudflareActions.cdn.setLoadingActivation(false));
    }
    yield put(cloudflareActions.cdn.succeededDeactivation());

    if (enqueueActivation) {
      yield put(cloudflareActions.cdn.activateCdn({
        hostingId: newHosting,
        domain,
        domainOrigin,
      }));
    }
  } catch (e) {
    const error = { sagasError: e, userRequestData: request, errorMessage: e.message };
    logger.error('Error sagas activateCdn', error);
    yield put(cloudflareActions.cdn.setActivatedDomain({
      domain,
      status: 'failure',
      requestStatus: 'failure',
    }));
    yield put(cloudflareActions.cdn.faliedDeactivation());
    yield put(cloudflareActions.cdn.setLoadingActivation(false));
  }
}

function* requestStatusOfFeatures(action) {
  const { payload } = action;
  const { cloudflareInternalId } = payload;


  const {
    token: whmcsToken,
  } = yield select(state => state.auth);

  const url = `/api/v3/cloudflare/features/${cloudflareInternalId}`;

  const headers = {
    Authorization: `Bearer ${whmcsToken}`,
    'x-brand': COUNTRY,
  };

  const request = { url, headers };

  yield put(cloudflareActions.features.loading(true));
  try {
    const response = yield call(api.whmcs.get, request);

    const assembled = assembledFeatures(response.data.data);
    yield put(cloudflareActions.features.set(assembled));
    yield put(cloudflareActions.features.success());
  } catch (e) {
    const error = { sagasError: e, userRequestData: request, errorMessage: e.message };
    logger.error('Error sagas requestStatusOfFeatures', error);
    yield put(cloudflareActions.features.failure());
  } finally {
    yield put(cloudflareActions.features.loading(false));
  }
}

function* requestUpdateFeature(action) {
  const { payload } = action;
  const { cloudflareInternalId, feature, status } = payload;


  const {
    token: whmcsToken,
  } = yield select(state => state.auth);

  const url = `/api/v3/cloudflare/features/${cloudflareInternalId}`;

  const bodyObject = {
    feature,
    switch: status,
  };

  const headers = {
    Authorization: `Bearer ${whmcsToken}`,
    'x-brand': COUNTRY,
  };

  const request = { url, headers, data: bodyObject };

  yield put(cloudflareActions.features.update.loading({ isLoading: true, feature }));
  try {
    const response = yield call(api.whmcs.patch, request);
    yield put(cloudflareActions.features.update.set({ data: response.data, feature, status }));
    yield put(cloudflareActions.features.update.success());
  } catch (e) {
    const error = { sagasError: e, userRequestData: request, errorMessage: e.message };
    logger.error('Error sagas requestStatusOfFeatures', error);
    if (e && e.response && e.response.data && e.response.data.data && e.response.data.data.blocked_until) {
      yield put(cloudflareActions.features.update.set({ data: e.response.data, feature }));
    }
    yield put(cloudflareActions.features.update.failure());
  } finally {
    yield put(cloudflareActions.features.update.loading({ isLoading: false, feature }));
  }
}

function* requestPurgeCache(action) {
  const { payload } = action;
  const { cloudflareInternalId } = payload;


  const {
    token: whmcsToken,
  } = yield select(state => state.auth);

  const url = `/api/v3/cloudflare/zone/purge_cache/${cloudflareInternalId}`;

  const headers = {
    Authorization: `Bearer ${whmcsToken}`,
    'x-brand': COUNTRY,
  };

  const request = { url, headers };

  yield put(cloudflareActions.purgeCache.loading(true));
  try {
    const response = yield call(api.whmcs.post, request);
    yield put(cloudflareActions.purgeCache.set(response.data));
    yield put(cloudflareActions.purgeCache.success());
  } catch (e) {
    const error = { sagasError: e, userRequestData: request, errorMessage: e.message };
    logger.error('Error sagas requestPurgeCache', error);
    if (e && e.response && e.response.data && e.response.data.data && e.response.data.data.blocked_until) {
      yield put(cloudflareActions.purgeCache.set(e.response.data));
    }
    yield put(cloudflareActions.purgeCache.failure());
  } finally {
    yield put(cloudflareActions.purgeCache.loading(false));
  }
}

function* requestSitesWizardDomainData(action) {
  const { payload } = action;
  const { domain, skipFinally } = payload;


  const {
    token: whmcsToken,
    jwt: jwtToken,
  } = yield select(state => state.auth);

  const url = `/api/v3/cloudflare/check_domain/${domain}`;

  const headers = {
    Authorization: `Bearer ${whmcsToken}`,
    'X-Service-Token': jwtToken,
  };

  const request = { url, headers };

  yield put(cloudflareActions.domains.domainsLoading(true));
  try {
    const response = yield call(api.whmcs.get, request);
    yield put(cloudflareActions.sitesWizard.setDomainsData(response.data.data));
    yield put(cloudflareActions.sitesWizard.requestSucceeded());
  } catch (e) {
    yield put(cloudflareActions.sitesWizard.requestFailed());
  } finally {
    if (!skipFinally) {
      yield put(cloudflareActions.domains.domainsLoading(false));
    }
  }
}

function* getSpeedAnalysisHistory(action) {
  const { payload } = action;

  const {
    cloudflareInternalId, perPage = 5, currentPage = 1, enqueued, updateLastAnalysis,
  } = payload;

  const {
    token: whmcsToken,
    jwt: jwtToken,
  } = yield select(state => state.auth);

  const { lastAnalysisId } = yield select(state => state.cloudflare.analysis.speedHistory);

  const url = `/api/v3/cloudflare/analytics/${cloudflareInternalId}/list_page_speed_test?per_page=${perPage}&current_page=${currentPage}`;

  const request = {
    url,
    headers: {
      Authorization: `Bearer ${whmcsToken}`,
      'X-Service-Token': jwtToken,
    },
  };

  if (updateLastAnalysis) {
    yield put(cloudflareActions.analysis.setLoadingAnalysis({ bool: true }));
  }

  yield put(cloudflareActions.analysis.setLoadingHistory(true));

  try {
    const response = yield call(api.whmcs.get, request);

    const {
      toggles,
    } = yield select(state => state.common);

    const { betweenRequest: cloudflareDelay } = toggles[CHARLIE_CLOUDFLARE_TIMERS] || { betweenRequest: 20000 };

    if (!enqueued) {
      yield put(cloudflareActions.analysis.setSpeedHistory(response.data.data));
      yield put(cloudflareActions.analysis.success());
      yield put(cloudflareActions.analysis.setLoadingAnalysis({ bool: false }));
      yield put(cloudflareActions.analysis.setLoadingHistory(false));
    }

    const lastExecution = response.data.data.results.find(item => item.id === lastAnalysisId);

    if (updateLastAnalysis) {
      yield put(cloudflareActions.analysis.setLastAnalysis(lastExecution));
    }

    if (enqueued && lastAnalysisId) {
      const isComplete = lastExecution
        && lastExecution.desktopReport.state && lastExecution.desktopReport.state.toLowerCase() === 'complete'
        && lastExecution.mobileReport.state && lastExecution.mobileReport.state.toLowerCase() === 'complete';

      const isFailed = lastExecution && (
        (lastExecution.desktopReport.state && lastExecution.desktopReport.state.toLowerCase() === 'failed')
        || (lastExecution.mobileReport.state && lastExecution.mobileReport.state.toLowerCase() === 'failed')
      );

      if (!isComplete && !isFailed) {
        const delayInMs = cloudflareDelay;
        yield delay(delayInMs);
        yield put(cloudflareActions.analysis.getSpeedHistory({ cloudflareInternalId, enqueued: true, updateLastAnalysis }));
      }

      if (isFailed) {
        yield put(cloudflareActions.analysis.setSpeedHistory(response.data.data));
        yield put(cloudflareActions.analysis.success());
        yield put(cloudflareActions.analysis.setLoadingAnalysis({ bool: false }));
        yield put(cloudflareActions.analysis.setLoadingHistory(false));
      }

      if (isComplete) {
        yield put(cloudflareActions.analysis.setSpeedHistory(response.data.data));
        yield put(cloudflareActions.analysis.success());
        yield put(cloudflareActions.analysis.setLoadingAnalysis({ bool: false }));
        yield put(cloudflareActions.analysis.setLoadingHistory(false));
      }
    }
    if (!lastAnalysisId) {
      yield put(cloudflareActions.analysis.setLoadingAnalysis({ bool: false }));
      yield put(cloudflareActions.analysis.setLoadingHistory(false));
    }
  } catch (e) {
    yield put(cloudflareActions.analysis.getAnalysisHistoryFailed());
    yield put(cloudflareActions.analysis.failure());
    yield put(cloudflareActions.analysis.setLoadingAnalysis({ bool: false }));
    yield put(cloudflareActions.analysis.setLoadingHistory(false));
  }
}

function* postSpeedAnalysis(action) {
  const { payload } = action;
  const { cloudflareInternalId } = payload;

  const {
    token: whmcsToken,
    jwt: jwtToken,
  } = yield select(state => state.auth);

  const url = `/api/v3/cloudflare/analytics/${cloudflareInternalId}/start_page_speed_test`;

  const request = {
    url,
    headers: {
      Authorization: `Bearer ${whmcsToken}`,
      'X-Service-Token': jwtToken,
    },
  };

  yield put(cloudflareActions.analysis.setLoadingAnalysis({ bool: true }));
  try {
    const response = yield call(api.whmcs.post, request);
    yield put(cloudflareActions.analysis.setLastAnalysisId(response.data.data));
    yield put(cloudflareActions.analysis.success());
    yield put(cloudflareActions.analysis.getSpeedHistory({ cloudflareInternalId, enqueued: true, updateLastAnalysis: true }));
  } catch (e) {
    yield put(cloudflareActions.analysis.failure());
    yield put(cloudflareActions.analysis.setLoadingAnalysis({ bool: false }));
  }
}

function* getVisitorsAnalysis(action) {
  const { payload } = action;
  const { cloudflareInternalId, days = 7 } = payload;


  const {
    token: whmcsToken,
    jwt: jwtToken,
  } = yield select(state => state.auth);

  const url = `/api/v3/cloudflare/analytics/${cloudflareInternalId}/http_traffic?days=${days}`;

  const request = {
    url,
    headers: {
      Authorization: `Bearer ${whmcsToken}`,
      'X-Service-Token': jwtToken,
    },
  };

  yield put(cloudflareActions.analysis.setLoadingAnalysis({ bool: true }));
  try {
    const response = yield call(api.whmcs.get, request);

    yield put(cloudflareActions.analysis.setVisitorAnalysis(response.data.data));
    yield put(cloudflareActions.analysis.setVisitorsSuccess());
    yield put(cloudflareActions.analysis.setLoadingAnalysis({ bool: false }));
  } catch (e) {
    yield put(cloudflareActions.analysis.setVisitorsFailure());
    yield put(cloudflareActions.analysis.setLoadingAnalysis({ bool: false }));
  }
}

function* getRules(action) {
  const { cloudflareInternalId } = action.payload;

  const {
    token: whmcsToken,
    jwt: jwtToken,
  } = yield select(state => state.auth);

  const url = `/api/v3/cloudflare/security/${cloudflareInternalId}/waf_custom_rules`;

  const request = {
    url,
    headers: {
      Authorization: `Bearer ${whmcsToken}`,
      'X-Service-Token': jwtToken,
    },
  };

  yield put(cloudflareActions.security.setRulesLoading(true));
  try {
    const response = yield call(api.whmcs.get, request);
    const rulesGroupId = response.data.data.custom_rules_group_id;
    const rulesList = assembledRulesList(response.data.data.rules);

    yield put(cloudflareActions.security.setRules({ rulesGroupId, rulesList }));
    yield put(cloudflareActions.security.getRulesSuccess());
    yield put(cloudflareActions.security.setRulesLoading(false));
  } catch (e) {
    let noRules = false;
    if (e && e.response && e.response.status === 404 && e.response.data && e.response.data.data && e.response.data.data.code === 'rules_not_found') {
      noRules = true;
    }

    if (noRules) {
      yield put(cloudflareActions.security.setRules({ rulesGroupId: '', rulesList: [] }));
      yield put(cloudflareActions.security.getRulesSuccess());
    }

    if (!noRules) {
      yield put(cloudflareActions.security.getRulesFailure());
    }

    yield put(cloudflareActions.security.setRulesLoading(false));
  }
}

function* addNewRule(action) {
  const { ruleData, cloudflareInternalId } = action.payload;
  const { description, lockType, value } = ruleData;

  const { rules: rulesState } = yield select(state => state.cloudflare);

  const {
    token: whmcsToken,
    jwt: jwtToken,
  } = yield select(state => state.auth);

  const url = `/api/v3/cloudflare/security/${cloudflareInternalId}/waf_custom_rules`;

  const request = {
    url,
    headers: {
      Authorization: `Bearer ${whmcsToken}`,
      'X-Service-Token': jwtToken,
    },
    data: {
      description,
      lock_type: lockType,
      value,
    },
  };

  yield put(cloudflareActions.security.setAddRuleLoading(true));
  try {
    const response = yield call(api.whmcs.post, request);
    const { rules: rulesData } = response.data.data;

    const newRule = assembledNewRule(rulesState.list, rulesData);

    const currentTimestamp = Math.floor(new Date().getTime() / 1000);
    const blockedUntil = currentTimestamp + 60;

    if (newRule) {
      yield put(cloudflareActions.security.addRuleSuccess({ newRule, blockedUntil }));
    }
    yield put(cloudflareActions.security.setAddRuleLoading(false));
    yield put(cloudflareActions.security.getRules({ cloudflareInternalId }));
  } catch (e) {
    let blockedUntil = 0;
    if (e && e.response && e.response.data && e.response.data.data && e.response.data.data.blocked_until) {
      blockedUntil = e.response.data.data.blocked_until;
    }
    yield put(cloudflareActions.security.addRuleFailure({ blockedUntil }));
  } finally {
    yield put(cloudflareActions.security.setAddRuleLoading(false));
  }
}

function* deleteSecurityRule(action) {
  const { ruleId, rulesGroupId, cloudflareInternalId } = action.payload;

  const {
    token: whmcsToken,
    jwt: jwtToken,
  } = yield select(state => state.auth);

  const { rules: rulesState } = yield select(state => state.cloudflare);

  const url = `/api/v3/cloudflare/security/${cloudflareInternalId}/waf_custom_rules/${rulesGroupId}/${ruleId}`;

  const request = {
    url,
    headers: {
      Authorization: `Bearer ${whmcsToken}`,
      'X-Service-Token': jwtToken,
    },
  };

  yield put(cloudflareActions.security.setDeleteRuleLoading(true));
  try {
    yield call(api.whmcs.useDelete, request);
    const newList = deleteRuleOnList({ currentRulesList: rulesState.list, ruleId });
    yield put(cloudflareActions.security.deleteRuleSuccess(newList));
    yield put(cloudflareActions.security.setDeleteRuleLoading(false));
    yield put(cloudflareActions.security.getRules({ cloudflareInternalId }));
  } catch (e) {
    yield put(cloudflareActions.security.setDeleteRuleLoading(false));
  }

  yield put(cloudflareActions.security.setDeleteRuleLoading(false));
}

function* editSecurityRule(action) {
  const {
    ruleData, rulesGroupId, ruleId, cloudflareInternalId,
  } = action.payload;

  const {
    token: whmcsToken,
    jwt: jwtToken,
  } = yield select(state => state.auth);

  const { rules: rulesState } = yield select(state => state.cloudflare);
  const currentRulesList = rulesState.list;

  const url = `/api/v3/cloudflare/security/${cloudflareInternalId}/waf_custom_rules/${rulesGroupId}/${ruleId}`;

  const request = {
    url,
    headers: {
      Authorization: `Bearer ${whmcsToken}`,
      'X-Service-Token': jwtToken,
    },
    data: ruleData,
  };

  yield put(cloudflareActions.security.setEditRuleLoading(true));
  yield put(cloudflareActions.security.setSingleRuleLoading({ ruleId, isLoading: true }));
  try {
    const currentTimestamp = Math.floor(new Date().getTime() / 1000);
    const blockedUntil = currentTimestamp + 60;

    const response = yield call(api.whmcs.patch, request);

    const editRuleOnListProps = {
      currentRulesList,
      newRulesList: response.data.data.rules,
      ruleId,
      blockedUntil,
    };

    const newList = editRuleOnList({ ...editRuleOnListProps });

    yield put(cloudflareActions.security.editRuleSet({ newList, success: true }));
    yield put(cloudflareActions.security.setRuleFormData({
      ruleData: {
        rule_id: '', description: '', lock_type: '', value: '', enabled: false,
      },
      submitAction: 'create',
    }));
  } catch (e) {
    if (e && e.response && e.response.data && e.response.data.data && e.response.data.data.blocked_until) {
      const blockedUntil = e.response.data.data.blocked_until;

      const editRuleOnListProps = {
        currentRulesList,
        newRulesList: currentRulesList,
        ruleId,
        blockedUntil,
      };

      const newList = editRuleOnList(editRuleOnListProps);
      yield put(cloudflareActions.security.editRuleSet({ newList, success: false }));
    }
  } finally {
    yield put(cloudflareActions.security.setSingleRuleLoading({ ruleId, isLoading: false }));
    yield put(cloudflareActions.security.setEditRuleLoading(false));
  }
}

function* toggleRuleStatus(action) {
  const {
    ruleData, rulesGroupId, ruleId, cloudflareInternalId,
  } = action.payload;

  const {
    token: whmcsToken,
    jwt: jwtToken,
  } = yield select(state => state.auth);

  const { rules: rulesState } = yield select(state => state.cloudflare);
  const currentRulesList = rulesState.list;

  const url = `/api/v3/cloudflare/security/${cloudflareInternalId}/waf_custom_rules/${rulesGroupId}/${ruleId}`;

  const request = {
    url,
    headers: {
      Authorization: `Bearer ${whmcsToken}`,
      'X-Service-Token': jwtToken,
    },
    data: ruleData,
  };

  yield put(cloudflareActions.security.setSingleRuleLoading({ ruleId, isLoading: true }));
  try {
    const currentTimestamp = Math.floor(new Date().getTime() / 1000);
    const blockedUntil = currentTimestamp + 60;

    const response = yield call(api.whmcs.patch, request);

    const editRuleOnListProps = {
      currentRulesList,
      newRulesList: response.data.data.rules,
      ruleId,
      blockedUntil,
    };

    const newList = editRuleOnList({ ...editRuleOnListProps });
    yield put(cloudflareActions.security.toggleRuleStatusSet({ newList }));
  } catch (e) {
    if (e && e.response && e.response.data && e.response.data.data && e.response.data.data.blocked_until) {
      const blockedUntil = e.response.data.data.blocked_until;

      const editRuleOnListProps = {
        currentRulesList,
        newRulesList: currentRulesList,
        ruleId,
        blockedUntil,
      };

      const newList = editRuleOnList({ ...editRuleOnListProps });
      yield put(cloudflareActions.security.toggleRuleStatusSet({ newList }));
    }
    yield put(cloudflareActions.security.toggleRuleStatusFailure());
  } finally {
    yield put(cloudflareActions.security.setSingleRuleLoading({ ruleId, isLoading: false }));
  }
}

function* sagas() {
  yield takeLatest(CloudflareActionTypes.DOMAINS_REQUEST, requestDomains);
  yield takeLatest(CloudflareActionTypes.ACTIVE_DOMAINS_REQUEST, requestActiveDomains);
  yield takeLatest(CloudflareActionTypes.ACTIVATE_CDN, activateCdn);
  yield takeLatest(CloudflareActionTypes.DEACTIVATE_CDN, deactivateCdn);
  yield takeLatest(CloudflareActionTypes.STATUS_OF_FEATURES_REQUEST, requestStatusOfFeatures);
  yield takeLatest(CloudflareActionTypes.UPDATE_FEATURE_REQUEST, requestUpdateFeature);
  yield takeLatest(CloudflareActionTypes.REQUEST_PURGE_CACHE, requestPurgeCache);
  yield takeLatest(CloudflareActionTypes.SITES_WIZARD_REQUEST_DOMAIN_DATA, requestSitesWizardDomainData);
  yield takeLatest(CloudflareActionTypes.SPEED_ANALYTICS_REQUEST, postSpeedAnalysis);
  yield takeLatest(CloudflareActionTypes.SPEED_ANALYTICS_GET_HISTORY, getSpeedAnalysisHistory);
  yield takeLatest(CloudflareActionTypes.VISITORS_ANALYSIS_REQUEST, getVisitorsAnalysis);
  yield takeLatest(CloudflareActionTypes.ADD_RULE_REQUEST, addNewRule);
  yield takeLatest(CloudflareActionTypes.GET_RULES_REQUEST, getRules);
  yield takeLatest(CloudflareActionTypes.DELETE_RULE_REQUEST, deleteSecurityRule);
  yield takeLatest(CloudflareActionTypes.EDIT_RULE_REQUEST, editSecurityRule);
  yield takeLatest(CloudflareActionTypes.TOGGLE_RULE_STATUS_REQUEST, toggleRuleStatus);
}

export default sagas;
